/**
 * The Alpine half of <x-insights.dropdown>: one choice from a short list, in the Insights dropdown style
 * Stats' Move to topic was drawn in, for the controls that were native
 * selects (Ola's QA, 9 Oct: Stats' Interval and each chart's own Interval switch, then a topic's Merge
 * into, the goal dialog's Add a key event, Target keywords' topic filter and a page report's Search type).
 *
 * A disclosure, not an ARIA menu or listbox (accessibility.md §9): the button
 * states the panel with aria-expanded and aria-controls, and the panel is a labelled group of plain
 * buttons, the current one marked aria-current and by a tick, so it promises no menu keyboard contract.
 * - The button, or Down or Up on it, opens the panel, and focus moves to the current choice.
 * - Up and Down step through the choices, stopping at either end; Home and End go to them.
 * - A pick, or Escape, closes it and returns focus to the button. Escape goes no further, so a pinned
 *   bar or a dialog around it stays as it was.
 * - Tab past the last choice, or Shift+Tab before the first, moves on as it would anyway (the panel
 *   sits right after its button) and closes the panel behind it; a press outside closes it and leaves
 *   focus where the press put it. Not a focusout: Safari moves focus to <body> on a press on a button,
 *   which would close the panel under the pointer before its click landed.
 *
 * A pick is told to the page as a bubbling `insights-dropdown-pick` {value} from the dropdown's root,
 * and only when it is not the current choice; what a pick means is the page's (the filter bar's
 * setInterval(), a chart card's interval-pick, a Livewire property a select's wire:model used to set).
 *
 * The panel is shown by a class, not x-show: x-show shows an element a frame late, and the focus sent
 * into it as it opens would land on a hidden button and fall to <body> (the date filter host's finding).
 *
 * A fixed panel (the component's fixed, inside a dialog whose body scrolls) is placed in the window by
 * x-anchor, where min-w-full would be the window's width, so it is drawn at least as wide as the button
 * was as it opened (dd_width).
 *
 * With a find box (the component's searchLabel, D188), the panel opens on the box, cleared, and shows only the choices
 * whose label, or `find` when a choice carries one, holds what is typed, in any case (dd_found()). Down in the box goes
 * to the first choice shown and Up on that choice back to the box; Enter in the box picks the first choice shown;
 * Shift+Tab from the box leaves the panel as it would from its first choice.
 *
 * Prefix: dd_. It is mixed into whatever scope it sits in, whose options and choice it reads.
 */
export const PICK_EVENT = 'insights-dropdown-pick';

export default function InsightsDropdown() {
    return {
        dd_isOpen: false,
        dd_width: 0,
        dd_query: '',

        /** The button opens the panel, and closes it when it is open. */
        dd_toggle() {
            if (this.dd_isOpen) {
                this.dd_close(true);

                return;
            }

            this.dd_open();
        },

        /** Open, focus on the find box when there is one, else the current choice (else the first), or the last one for Up. */
        dd_open(fromEnd = false) {
            this.dd_width = this.$refs.ddTrigger?.offsetWidth ?? 0;
            this.dd_query = '';
            this.dd_isOpen = true;
            this.$nextTick(() => {
                if (this.$refs.ddFind) {
                    this.$refs.ddFind.focus();

                    return;
                }

                const choices = this.dd_choices();
                const current = choices.find((choice) => choice.getAttribute('aria-current') === 'true');

                (current ?? (fromEnd ? choices.at(-1) : choices[0]))?.focus();
            });
        },

        /** Closed; focus goes back to the button only when it was in the panel or a key closed it. */
        dd_close(refocus = false) {
            if (!this.dd_isOpen) return;

            const hadFocus = this.$refs.ddPanel?.contains(document.activeElement) ?? false;

            this.dd_isOpen = false;

            if (refocus || hadFocus) this.$refs.ddTrigger?.focus();
        },

        /**
         * Tab past either end of the panel: focus moves on, and the panel closes behind it. A task
         * later, once the browser has moved focus: hidden first, the focused choice would drop focus
         * to <body>, and Tab would move on from the top of the page.
         */
        dd_tabOut(event) {
            const choices = this.dd_choices();
            const first = this.$refs.ddFind ?? choices[0];
            const edge = event.shiftKey ? first : (choices.at(-1) ?? this.$refs.ddFind);

            if (edge && document.activeElement !== edge) return;

            setTimeout(() => {
                this.dd_isOpen = false;
            }, 0);
        },

        /** A choice picked: the panel closes, focus is back on the button, and a new choice is told. */
        dd_pick(value, current) {
            this.dd_close(true);

            if (value === current) return;

            this.$root.dispatchEvent(new CustomEvent(PICK_EVENT, { bubbles: true, detail: { value } }));
        },

        /** Up and Down move through the choices, stopping at either end; Up on the first goes back to the find box. */
        dd_step(delta) {
            const choices = this.dd_choices();
            const index = choices.indexOf(document.activeElement);

            if (index === 0 && delta < 0 && this.$refs.ddFind) {
                this.$refs.ddFind.focus();

                return;
            }

            choices[Math.min(choices.length - 1, Math.max(0, index + delta))]?.focus();
        },

        /** The choices the find box leaves: every one while it is empty. */
        dd_found(options) {
            const query = this.dd_query.trim().toLocaleLowerCase();

            if (query === '') return options;

            return options.filter((option) => String(option.find ?? option.label).toLocaleLowerCase().includes(query));
        },

        /** Home and End: the first choice, or the last. */
        dd_edge(last = false) {
            const choices = this.dd_choices();

            (last ? choices.at(-1) : choices[0])?.focus();
        },

        dd_choices() {
            return Array.from(this.$refs.ddPanel?.querySelectorAll('[data-insights-dropdown-choice]') ?? []);
        },
    };
}
